import { DUMMY_PLACE_GROUP_ID, SamplesDeliveryType } from '@caresend/types';
import { isOrderComplete } from './draftWaypoint.js';
import { getUnknownErrorMessage } from '../error/errorHandling.js';
import { timeInMsToHoursAndMinutes } from '../formatters/date.js';
import { HOUR } from '../initializers/date.js';
import { deduplicateArray, arrayToObj } from '../languageExtensions/array.js';
import { isNullish, nullishFilter } from '../typeValidation/nullish.js';

/**
 * Gets the order descriptor for a draft waypoint.
 */

const getOrderDescriptor = draftWaypoint => isOrderComplete(draftWaypoint) ? 'full' : 'draft';
const getIsMailIn = (timeSlot, visitPlaceGroupID) => {
  var _a, _b, _c;

  return !((_c = (_b = (_a = timeSlot.placeGroups) === null || _a === void 0 ? void 0 : _a[visitPlaceGroupID]) === null || _b === void 0 ? void 0 : _b.walkIn) === null || _c === void 0 ? void 0 : _c.isOpen);
};
/**
 * Get the place group IDs associated with the lab drop-off on the
 * procedures found on this visit. For draft orders, it may be possible that the
 * lab is not yet selected. For draft orders that are ready for scheduling, a
 * lab is always expected to have been selected.
 */

const getVisitPlaceGroupIDs = params => params.draftWaypoint.draftOrderID ? [DUMMY_PLACE_GROUP_ID] : deduplicateArray(Object.values(params.procedures).map(procedure => {
  var _a;

  return Object.keys((_a = procedure.dropOffPlaceGroups) !== null && _a !== void 0 ? _a : {});
}).flat());
/**
 * Get the non-draft procedure IDs that require mail-in for a given place group.
 */

const getNonDraftMailInProcedureIDs = (params, visitPlaceGroupID) => {
  const {
    draftWaypoint,
    procedures,
    timeSlot
  } = params;
  if (draftWaypoint.draftOrderID) return [];
  return procedures.filter(procedure => procedure.id && procedure.dropOffPlaceGroups && procedure.dropOffPlaceGroups[visitPlaceGroupID] && getIsMailIn(timeSlot, visitPlaceGroupID)).map(procedure => procedure.id);
};
/**
 * Assumes time slot has already been checked and walk-in is open. This
 * only checks non-draft procedures, as draft procedures do not have supply
 * config options.
 */

const getNonDraftProceduresWalkInConfigFailure = (params, visitPlaceGroupID) => {
  const {
    options
  } = params;
  const failures = params.procedures.map(procedure => {
    var _a, _b, _c;

    const procedureID = procedure.id;
    const procedureOptions = options[procedureID];
    const walkInConfigExists = !!((_c = (_b = (_a = procedureOptions === null || procedureOptions === void 0 ? void 0 : procedureOptions.supplyInstructionsByPlaceGroup) === null || _a === void 0 ? void 0 : _a[visitPlaceGroupID]) === null || _b === void 0 ? void 0 : _b.supplyInstructionsByDeliveryType) === null || _c === void 0 ? void 0 : _c.walkIn);

    if (!walkInConfigExists) {
      return `No walk-in supply config for procedure ${procedureID} ` + `matching place group ${visitPlaceGroupID}`;
    }

    return null;
  });
  if (failures.find(nullishFilter)) return null;
  if (failures.filter(nullishFilter).length) return failures.join(', ');
  return null;
};
/**
 * Returns lowest sample stability on the mail-in option for all of the
 * (non-draft) mail-inprocedures.
 */


const getLowestMailInSampleStability = (params, visitPlaceGroupID) => {
  const {
    options
  } = params;
  const isMailIn = getIsMailIn(params.timeSlot, visitPlaceGroupID);
  if (!isMailIn) return null;
  const nonDraftMailInProcedureIDs = getNonDraftMailInProcedureIDs(params, visitPlaceGroupID);
  if (!nonDraftMailInProcedureIDs.length) return null;
  const sampleStabilities = nonDraftMailInProcedureIDs.map(procedureID => {
    var _a, _b, _c, _d, _e;

    const sampleStability = (_e = (_d = (_c = (_b = (_a = options[procedureID]) === null || _a === void 0 ? void 0 : _a.supplyInstructionsByPlaceGroup) === null || _b === void 0 ? void 0 : _b[visitPlaceGroupID]) === null || _c === void 0 ? void 0 : _c.supplyInstructionsByDeliveryType) === null || _d === void 0 ? void 0 : _d.mailIn) === null || _e === void 0 ? void 0 : _e.sampleStability;
    if (sampleStability) return sampleStability;
  }).filter(nullishFilter);
  if (!sampleStabilities.length) return null;
  return Math.min(...sampleStabilities);
};
/**
 * Based on time slot start, `estimatedAcceptanceTimestamp` and `sampleStability`,
 * determine if the time slot is compatible with mailing to the lab and being
 * accepted before samples expire.
 */


const getMailInFailure = (params, visitPlaceGroupID) => {
  try {
    const {
      labShipmentInfoByPlaceGroup,
      timeSlot
    } = params;
    const isMailIn = getIsMailIn(timeSlot, visitPlaceGroupID);
    if (!isMailIn) throw Error('No mail-in option on timeslot');
    const sampleStability = getLowestMailInSampleStability(params, visitPlaceGroupID);

    if (!sampleStability) {
      const nonDraftMailInProcedureIDs = getNonDraftMailInProcedureIDs(params, visitPlaceGroupID);
      throw Error('Missing mail-in sample stability data in supply instruction config ' + `options on at least one of the following procedures: ${nonDraftMailInProcedureIDs.join(', ')}`);
    }

    const labShipmentInfo = labShipmentInfoByPlaceGroup === null || labShipmentInfoByPlaceGroup === void 0 ? void 0 : labShipmentInfoByPlaceGroup[visitPlaceGroupID];
    if (!labShipmentInfo) throw Error('No lab shipment info found');
    const estimatedAcceptanceTimestamp = labShipmentInfo.estimatedAcceptanceTimestamp;
    if (isNullish(estimatedAcceptanceTimestamp)) throw Error('No estimatedAcceptanceTimestamp found');
    const timeSlotStart = timeSlot.startTime;
    const stabilityInMs = sampleStability * HOUR;
    const stabilityExpiry = timeSlotStart + stabilityInMs;
    const mailInWorks = estimatedAcceptanceTimestamp < stabilityExpiry;

    if (!mailInWorks) {
      const pastDeadlineMs = estimatedAcceptanceTimestamp - stabilityExpiry;
      const pastDeadline = timeInMsToHoursAndMinutes(pastDeadlineMs);
      throw Error(`Mail-in acceptance ${pastDeadline} too late.`);
    }

    return null;
  } catch (error) {
    return `Mail-in failure for place group ${visitPlaceGroupID}: ${getUnknownErrorMessage(error)}`;
  }
};
/**
 * For a given visit, we must ensure the associated place group is listed on
 * the time slot and is open (hours of operation).
 *
 * Returns true if:
 *   - Place group is included on the time slot and either `walkIn` or `mailIn`
 *     is open
 *   - For full orders, matching `walkIn` or `mailIn` is available on the
 *     supply instruction config options.
 *   - If only `mailIn` is open, given time slot `startTime`,
 *     `estimatedAcceptanceTimestamp` and `sampleStability`, the time slot must
 *     be compatible with mailing to the lab and being accepted before samples
 *     expire.
 */

const getPlaceGroupsFailure = (params, placeGroupID) => {
  const {
    timeSlot
  } = params;
  const visitPlaceGroupIDs = placeGroupID ? [placeGroupID] : getVisitPlaceGroupIDs(params);
  const failures = visitPlaceGroupIDs.map(visitPlaceGroupID => {
    var _a, _b, _c;

    const placeGroup = (_a = timeSlot.placeGroups) === null || _a === void 0 ? void 0 : _a[visitPlaceGroupID];
    if (!placeGroup) return `Place group ${visitPlaceGroupID} not available on time slot`;

    if ((_b = placeGroup.walkIn) === null || _b === void 0 ? void 0 : _b.isOpen) {
      return getNonDraftProceduresWalkInConfigFailure(params, visitPlaceGroupID);
    }

    if ((_c = placeGroup.mailIn) === null || _c === void 0 ? void 0 : _c.isOpen) {
      return getMailInFailure(params, visitPlaceGroupID);
    }

    return null;
  }).filter(nullishFilter);
  if (failures.length) return failures.join(', ');
  return null;
};
const getSamplesDeliveryTypeByPlaceGroup = (params, isFallback) => {
  const {
    timeSlot
  } = params;
  const visitPlaceGroupIDs = isFallback ? [DUMMY_PLACE_GROUP_ID] : getVisitPlaceGroupIDs(params);
  const placeGroupsFailure = getPlaceGroupsFailure(params, isFallback ? DUMMY_PLACE_GROUP_ID : undefined);
  if (placeGroupsFailure) return null;
  return visitPlaceGroupIDs.reduce((acc, visitPlaceGroupID) => Object.assign(Object.assign({}, acc), {
    [visitPlaceGroupID]: getIsMailIn(timeSlot, visitPlaceGroupID) ? SamplesDeliveryType.MAIL_IN : SamplesDeliveryType.WALK_IN
  }), {});
};
const getSamplesDeliveryType = params => {
  const {
    timeSlot
  } = params;
  const visitPlaceGroupIDs = getVisitPlaceGroupIDs(params);
  const placeGroupsFailure = getPlaceGroupsFailure(params);
  if (placeGroupsFailure) return null;
  const placeGroups = visitPlaceGroupIDs.map(visitPlaceGroupID => {
    var _a;

    const placeGroup = (_a = timeSlot.placeGroups) === null || _a === void 0 ? void 0 : _a[visitPlaceGroupID];
    return placeGroup;
  }).filter(nullishFilter);
  if (!placeGroups.length) return null;
  const somePlaceGroupIsMailIn = placeGroups.some(placeGroup => {
    var _a;

    return !((_a = placeGroup === null || placeGroup === void 0 ? void 0 : placeGroup.walkIn) === null || _a === void 0 ? void 0 : _a.isOpen);
  });
  return somePlaceGroupIsMailIn ? SamplesDeliveryType.MAIL_IN : SamplesDeliveryType.WALK_IN;
};
/**
 * Returns:
 * - `true` if at least one procedure requires centrifuge
 * - `false` if no procedures require centrifuge
 * - `null` if data is not available to determine if procedures will require
 *   centrifuge (i.e. on a draft order, or on an ineligible slot). If the slot
 *   is eligible, the visit will be treated as if no centrifuge is required.
 *
 * TODO: refactor to support multiple place groups
 */

const getRequiresCentrifugeWithoutTimeSlot = (samplesDeliveryType, params) => {
  const {
    options
  } = params;
  const optionsArray = Object.entries(options);
  if (!optionsArray.length) return null;
  let missingOptionSet = false;
  const optionsForVisitWithPlaceGroupID = optionsArray.map(([procedureID, supplyInstructions]) => {
    var _a, _b, _c;

    const procedurePlaceGroupID = (_c = (_b = (_a = params.procedures.find(procedure => procedure.id === procedureID)) === null || _a === void 0 ? void 0 : _a.dropOffPlaceGroups) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.id;
    if (!procedurePlaceGroupID) return null;
    const option = supplyInstructions.supplyInstructionsByPlaceGroup[procedurePlaceGroupID];

    if (!option) {
      missingOptionSet = true;
      return null;
    }

    return {
      option,
      procedurePlaceGroupID
    };
  }).filter(nullishFilter);
  if (missingOptionSet) return null;
  const requiresCentrifuge = optionsForVisitWithPlaceGroupID.some(optionSet => {
    var _a;

    const option = optionSet.option.supplyInstructionsByDeliveryType[samplesDeliveryType];
    return !!((_a = option === null || option === void 0 ? void 0 : option.processingIncludes) === null || _a === void 0 ? void 0 : _a.centrifuge);
  });
  return requiresCentrifuge;
};
/**
 * Returns:
 * - `true` if at least one procedure requires centrifuge
 * - `false` if no procedures require centrifuge
 * - `null` if data is not available to determine if procedures will require
 *   centrifuge (i.e. on a draft order, or on an ineligible slot). If the slot
 *   is eligible, the visit will be treated as if no centrifuge is required.
 *
 * TODO: refactor to support multiple place groups
 */

const getRequiresCentrifuge = params => {
  const {
    options
  } = params;
  const optionsArray = Object.entries(options);
  if (!optionsArray.length) return null;
  const samplesDeliveryType = getSamplesDeliveryType(params);
  if (!samplesDeliveryType) return null;
  let missingOptionSet = false;
  const optionsForVisit = optionsArray.map(([procedureID, supplyInstructions]) => {
    var _a, _b, _c;

    const procedurePlaceGroupID = (_c = (_b = (_a = params.procedures.find(procedure => procedure.id === procedureID)) === null || _a === void 0 ? void 0 : _a.dropOffPlaceGroups) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.id;
    if (!procedurePlaceGroupID) return null;
    const optionSet = supplyInstructions.supplyInstructionsByPlaceGroup[procedurePlaceGroupID];

    if (!optionSet) {
      missingOptionSet = true;
      return null;
    }

    return optionSet;
  }).filter(nullishFilter);
  if (missingOptionSet) return null;
  const requiresCentrifuge = optionsForVisit.some(optionSet => {
    var _a;

    const option = optionSet.supplyInstructionsByDeliveryType[samplesDeliveryType];
    return !!((_a = option === null || option === void 0 ? void 0 : option.processingIncludes) === null || _a === void 0 ? void 0 : _a.centrifuge);
  });
  return requiresCentrifuge;
};
/**
 * Check if a given nurse has the required supplies.
 */

const getNurseHasSupplies = (nurse, requiredSupplies) => {
  if (nurse.allSupplies) return true;
  if (!nurse.supplies) return false;
  const nurseSupplyIDs = Object.keys(nurse.supplies);
  const notAllSuppliesMet = requiredSupplies.some(supplyID => !nurseSupplyIDs.includes(supplyID));
  return !notAllSuppliesMet;
};
/**
 * Return all eligible nurses in the array. The nurses must be active, they must
 * own a centrifuge if required, and if the scheduling config states that a
 * pending nurse can't be used, the pending nurse ID must be excluded. If
 * `usePendingNurseID` is not provided, it will be assumed to be `false`.
 */

const findEligibleNurses = (params, usePendingNurseID) => {
  var _a;

  const availableNurses = Object.values((_a = params.timeSlot.availableNurses) !== null && _a !== void 0 ? _a : {});
  const requiresCentrifuge = getRequiresCentrifuge(params);
  const eligibleNurseOptions = availableNurses.filter(availableNurse => {
    if (!availableNurse.active) return false;
    const isNursePendingNurse = availableNurse.nurseID === 'pendingNurseID';
    if (!usePendingNurseID && isNursePendingNurse) return false;
    if (requiresCentrifuge) return !!availableNurse.ownsCentrifuge;
    return true;
  });
  const rankedNurseOptions = eligibleNurseOptions.sort((a, b) => a.rank - b.rank);
  return arrayToObj(rankedNurseOptions, 'nurseID');
};
const getArrayOfAllTimeSlots = timeSlots => Object.values(timeSlots).flatMap(timeSlotsByDate => Object.values(timeSlotsByDate));
/**
 * Returns timeslots that are not disabled. This is useful during debug mode
 * when all timeslots get returned, but with the disabled flag set if
 * ineligible.
 *
 * Usage:
 * const eligibleTimeSlots = getEligibleTimeSlots(timeSlotsByDate);
 * console.info(eligibleTimeSlots);
 */

const getEligibleTimeSlots = timeSlotsByDate => {
  const eligibleTimeSlotsByDate = {};
  Object.entries(timeSlotsByDate).forEach(([date, timeSlots]) => {
    Object.entries(timeSlots).forEach(([time, timeSlot]) => {
      if (!timeSlot.disabled) {
        eligibleTimeSlotsByDate[date] = Object.assign(Object.assign({}, eligibleTimeSlotsByDate[date]), {
          [time]: timeSlot
        });
      }
    });
  });
  return eligibleTimeSlotsByDate;
};

export { findEligibleNurses, getArrayOfAllTimeSlots, getEligibleTimeSlots, getIsMailIn, getMailInFailure, getNonDraftMailInProcedureIDs, getNurseHasSupplies, getOrderDescriptor, getPlaceGroupsFailure, getRequiresCentrifuge, getRequiresCentrifugeWithoutTimeSlot, getSamplesDeliveryType, getSamplesDeliveryTypeByPlaceGroup, getVisitPlaceGroupIDs };
